import Swal from "sweetalert2";
import React, { useEffect, useState, useRef } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Alert,
  Spinner,
  Row,
  Col,
  Card,
  Carousel,
  ProgressBar,
} from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import "react-bootstrap-typeahead/css/Typeahead.css";
import {
  PlusCircle,
  PencilSquare,
  Trash,
  Gear,
  Building,
  ChevronLeft,
  ChevronRight,
} from "react-bootstrap-icons";
import axios from "axios";
import API_BASE_URL from "../config";

const ManageServices = ({ companyId }) => {
  const [services, setServices] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState([]);
  const [currentImages, setCurrentImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [showSlideshow, setShowSlideshow] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [overallProgress, setOverallProgress] = useState(0);
  const fileInputRef = useRef(null);
  const carouselRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    duration: 0,
    discount_code: "",
    price: "",
    branch_id: "",
    description: "",
  });

  // ✅ Handle keyboard navigation for slideshow
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showSlideshow) return;
      if (e.key === "ArrowRight" && carouselRef.current) {
        carouselRef.current.next();
      } else if (e.key === "ArrowLeft" && carouselRef.current) {
        carouselRef.current.prev();
      } else if (e.key === "Escape") {
        setShowSlideshow(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [showSlideshow]);

  // ✅ Fetch services
  const fetchServices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/services`
      );
      setServices(res.data || []);
    } catch (err) {
      console.error(err);
      setError("Failed to load services");
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to load services",
      });
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch employees
  const fetchEmployees = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/employees`
      );
      setEmployees(res.data || []);
    } catch (err) {
      console.error("Failed to load employees", err);
    }
  };

  // ✅ Fetch branches
  const fetchBranches = async () => {
    try {
      const res = await axios.get(
        `${API_BASE_URL}/companies/${companyId}/branches`
      );
      const allBranches = [
        { id: null, name: "All Branches" },
        ...(res.data || []),
      ];
      setBranches(allBranches);
    } catch (err) {
      console.error("Failed to load branches", err);
    }
  };

  useEffect(() => {
    if (!companyId) return;
    fetchServices();
    fetchEmployees();
    fetchBranches();
  }, [companyId]);

  // ✅ Handle form changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle employees select
  const handleEmployeesSelect = (selected) => {
    setSelectedEmployees(selected);
  };

  // ✅ Handle branch select
  const handleBranchSelect = (selected) => {
    setSelectedBranch(selected);
    setForm((prev) => ({ ...prev, branch_id: selected[0]?.id || null }));
  };

  // ✅ Handle new image change
  const handleNewImageChange = (index, field, value) => {
    const updated = [...newImages];
    updated[index][field] = value;
    setNewImages(updated);
  };

  // ✅ Handle image drop or selection
  const handleImageDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer ? e.dataTransfer.files : e.target.files;
    if (files.length) {
      const totalImages = currentImages.length + newImages.length + files.length;
      if (totalImages > 15) {
        setError("Maximum 15 images per service");
        return;
      }
      const newFiles = Array.from(files).map((file) => ({
        file,
        description: "",
        preview: URL.createObjectURL(file),
        progress: 0,
      }));
      setNewImages([...newImages, ...newFiles]);
    }
  };

  // ✅ Handle drag over
  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  // ✅ Remove new image
  const removeNewImage = (index) => {
    const updated = newImages.filter((_, i) => i !== index);
    setNewImages(updated);
  };

  // ✅ Toggle image selection
  const toggleImageSelection = (id) => {
    setSelectedImages((prev) =>
      prev.includes(id)
        ? prev.filter((imgId) => imgId !== id)
        : [...prev, id]
    );
  };

  // ✅ Select all images
  const selectAllImages = () => {
    setSelectedImages(currentImages.map((img) => img.id));
  };

  // ✅ Delete selected images
  const handleDeleteSelectedImages = async () => {
    if (selectedImages.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "No Images Selected",
        text: "Please select at least one image to delete.",
      });
      return;
    }

    const result = await Swal.fire({
      title: "Are you sure?",
      text: `You are about to delete ${selectedImages.length} image(s)!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete them!",
    });

    if (!result.isConfirmed) return;

    try {
      await Promise.all(
        selectedImages.map((id) =>
          axios.delete(`${API_BASE_URL}/service-images/${id}`)
        )
      );
      setCurrentImages(currentImages.filter((img) => !selectedImages.includes(img.id)));
      setSelectedImages([]);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Selected images have been deleted.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to delete images",
      });
    }
  };

  // ✅ Validate
  const validate = () => {
    if (!form.name) return "Service name is required";
    if (isNaN(form.duration)) return "Duration must be a number (minutes)";
    if (!form.price || isNaN(form.price)) return "Price must be a valid number";
    if (currentImages.length + newImages.length > 15)
      return "Maximum 15 images per service";
    for (let img of newImages) {
      if (!img.file) return "File is required for new images";
    }
    return null;
  };

  // ✅ Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const v = validate();
    if (v) return setError(v);

    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (v !== null && v !== undefined) formData.append(k, v);
    });

    if (selectedEmployees.length > 0) {
      selectedEmployees.forEach((emp) =>
        formData.append("employees[]", emp.id)
      );
    }

    newImages.forEach((img, index) => {
      formData.append("new_images[]", img.file);
      formData.append("new_descriptions[]", img.description);
    });

    try {
      const url = editingService
        ? `${API_BASE_URL}/services/update/${editingService.id}`
        : `${API_BASE_URL}/companies/${companyId}/services`;

      await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (progressEvent) => {
          const totalLength = progressEvent.total || progressEvent.loaded;
          const totalProgress = Math.round((progressEvent.loaded * 100) / totalLength);
          setOverallProgress(totalProgress);

          if (newImages.length > 0) {
            const perFileProgress = totalProgress / newImages.length;
            const updatedImages = newImages.map((img, index) => ({
              ...img,
              progress: Math.min(Math.round(perFileProgress * (index + 1)), 100),
            }));
            setNewImages(updatedImages);
          }
        },
      });

      Swal.fire({
        icon: "success",
        title: editingService ? "Updated!" : "Added!",
        text: `Service ${editingService ? "updated" : "added"} successfully`,
        timer: 1500,
        showConfirmButton: false,
      });

      setShowModal(false);
      setNewImages([]);
      setOverallProgress(0);
      fetchServices();
    } catch (err) {
      console.error(err);
      setError("Failed to save service");
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to save service",
      });
    }
  };

  // ✅ Edit
  const handleEdit = (service) => {
    setEditingService(service);

    const selectedEmps = service.employees
      ? service.employees
          .map((se) => employees.find((e) => e.id === se.id))
          .filter(Boolean)
      : [];

    const branch =
      branches.find((b) => b.id === service.branch_id) || branches[0];

    setForm({
      name: service.name || "",
      duration: service.duration_minutes || 0,
      discount_code: service.discount_code || "",
      price: service.price_cents || "",
      branch_id: branch?.id || null,
      description: service.description || "",
    });

    setSelectedEmployees(selectedEmps);
    setSelectedBranch(branch ? [branch] : []);
    setCurrentImages(service.images || []);
    setNewImages([]);
    setSelectedImages([]);
    setOverallProgress(0);
    setShowModal(true);
  };

  // ✅ Delete service
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE_URL}/services/${id}`);
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Service has been deleted.",
        timer: 1500,
        showConfirmButton: false,
      });
      fetchServices();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to delete service",
      });
    }
  };

  // ✅ Delete image
  const handleDeleteImage = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this image!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    });

    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_BASE_URL}/service-images/${id}`);
      setCurrentImages(currentImages.filter((img) => img.id !== id));
      setSelectedImages(selectedImages.filter((imgId) => imgId !== id));
      Swal.fire({
        icon: "success",
        title: "Deleted!",
        text: "Image has been deleted.",
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "Failed to delete image",
      });
    }
  };

  // ✅ Add new
  const handleAdd = () => {
    setEditingService(null);
    setForm({
      name: "",
      duration: 0,
      discount_code: "",
      price: "",
      branch_id: "",
      description: "",
    });
    setSelectedEmployees([]);
    setSelectedBranch(branches.length ? [branches[0]] : []);
    setCurrentImages([]);
    setNewImages([]);
    setSelectedImages([]);
    setOverallProgress(0);
    setShowModal(true);
  };

  return (
    <div className="container-fluid mt-3 px-3 px-md-4">
      {/* ✅ Mobile Responsive Header */}
      <Row className="align-items-center mb-3">
        <Col xs={12} md={6}>
          <h2 className="mb-0">
            <Gear className="me-2" /> Manage Services
          </h2>
        </Col>
        <Col xs={12} md={6} className="text-end">
          <Button
            variant="primary"
            size="sm"
            onClick={handleAdd}
            className="d-flex align-items-center gap-1"
          >
            <PlusCircle /> Add Service
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading services...
        </div>
      ) : (
        <Card className="shadow-sm border-0">
          <div className="table-responsive">
            <Table striped hover className="mb-0">
              <thead className="table-dark">
                <tr>
                  <th className="col-3">Service Name</th>
                  <th className="col-2 d-none d-md-table-cell">Provider</th>
                  <th className="col-1 d-none d-lg-table-cell">Duration</th>
                  <th className="col-1">Branch</th>
                  <th className="col-1 d-none d-md-table-cell">Price</th>
                  <th className="col-1 d-none d-md-table-cell">Discount</th>
                  <th className="col-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {services.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <Building className="mb-2" size={48} />
                      <p className="text-muted mb-0">No services found</p>
                    </td>
                  </tr>
                ) : (
                  services.map((s) => (
                    <tr key={s.id}>
                      <td>{s.name}</td>
                      <td className="d-none d-md-table-cell">
                        {s.employees?.map((e) => e.name).join(", ") || "—"}
                      </td>
                      <td className="d-none d-lg-table-cell">
                        {s.duration_minutes} min
                      </td>
                      <td>{s.branch_name || "All"}</td>
                      <td className="d-none d-md-table-cell">
                        ₹{s.price_cents}
                      </td>
                      <td className="d-none d-md-table-cell">
                        {s.discount_code || "—"}
                      </td>
                      <td>
                        <div className="d-flex flex-column flex-md-row gap-1">
                          <Button
                            size="sm"
                            variant="outline-warning"
                            className="d-flex align-items-center gap-1 btn-icon-text"
                            onClick={() => handleEdit(s)}
                            title="Edit Service"
                          >
                            <PencilSquare size={12} />
                            <span className="d-none d-md-inline">Edit</span>
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            className="d-flex align-items-center gap-1 btn-icon-text"
                            onClick={() => handleDelete(s.id)}
                            title="Delete Service"
                          >
                            <Trash size={12} />
                            <span className="d-none d-md-inline">Delete</span>
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </Card>
      )}

      {/* ✅ Add/Edit Modal - Mobile Optimized */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>
              <Gear className="me-2" />
              {editingService ? "Edit Service" : "Add Service"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col xs={12} className="mb-3">
                <Form.Label className="fw-bold">
                  Service Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} className="mb-3">
                <Form.Label className="fw-bold">Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">Branch</Form.Label>
                <Typeahead
                  id="branch-select"
                  labelKey={(b) => b.name}
                  options={branches}
                  selected={selectedBranch}
                  onChange={handleBranchSelect}
                  placeholder="Select branch..."
                  clearButton
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">Providers</Form.Label>
                <Typeahead
                  id="employee-provider"
                  multiple
                  labelKey={(e) => `${e.first_name} ${e.last_name}`}
                  options={employees}
                  selected={selectedEmployees}
                  onChange={handleEmployeesSelect}
                  placeholder="Select providers..."
                  clearButton
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  Duration (minutes) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  required
                />
              </Col>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label className="fw-bold">
                  Price (₹) <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="number"
                  name="price"
                  value={form.price}
                  onChange={handleChange}
                  required
                />
              </Col>
            </Row>

            <Row>
              <Col xs={12} md={6} className="mb-3">
                <Form.Label>Discount Code</Form.Label>
                <Form.Control
                  name="discount_code"
                  value={form.discount_code}
                  onChange={handleChange}
                />
              </Col>
            </Row>

            {/* ✅ Current Images */}
            {editingService && currentImages.length > 0 && (
              <>
                <h5 className="mb-3">Current Images</h5>
                <div className="mb-3">
                  <Button
                    variant="info"
                    size="sm"
                    className="me-2"
                    onClick={() => setShowSlideshow(true)}
                  >
                    View Slideshow
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleDeleteSelectedImages}
                    disabled={selectedImages.length === 0}
                  >
                    Delete Selected ({selectedImages.length})
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="ms-2"
                    onClick={selectAllImages}
                  >
                    Select All
                  </Button>
                </div>
                <Row>
                  {currentImages.map((img) => (
                    <Col md={4} key={img.id} className="mb-3">
                      <Card
                        className={selectedImages.includes(img.id) ? "border border-primary" : ""}
                      >
                        <Card.Img
                          variant="top"
                          src={`${API_BASE_URL}/uploads/${editingService.id}/${img.image_path}`}
                          style={{ height: "150px", objectFit: "cover" }}
                        />
                        <Card.Body>
                          <Form.Check
                            type="checkbox"
                            label={img.description || "No description"}
                            checked={selectedImages.includes(img.id)}
                            onChange={() => toggleImageSelection(img.id)}
                            className="mb-2"
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => handleDeleteImage(img.id)}
                          >
                            Delete
                          </Button>
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              </>
            )}

            {/* ✅ New Images - Drag and Drop */}
            <h5 className="mb-3">Add New Images (Max 15 total)</h5>
            <div
              onDrop={handleImageDrop}
              onDragOver={handleDragOver}
              className="border border-dashed border-primary p-4 mb-3 text-center"
              style={{ minHeight: "100px", backgroundColor: "#f8f9fa" }}
            >
              <p className="mb-2">Drag and drop images here or click to select</p>
              <Button
                variant="primary"
                size="sm"
                onClick={() => fileInputRef.current.click()}
              >
                Select Images
              </Button>
              <Form.Control
                type="file"
                multiple
                accept="image/*"
                onChange={handleImageDrop}
                ref={fileInputRef}
                style={{ display: "none" }}
              />
            </div>

            {/* ✅ Overall Upload Progress */}
            {newImages.length > 0 && overallProgress > 0 && (
              <div className="mb-3">
                <Form.Label>Overall Upload Progress</Form.Label>
                <ProgressBar now={overallProgress} label={`${overallProgress}%`} />
              </div>
            )}

            {/* ✅ Preview and Description for New Images */}
            {newImages.length > 0 && (
              <Row>
                {newImages.map((img, index) => (
                  <Col md={4} key={index} className="mb-3">
                    <Card>
                      <Card.Img
                        variant="top"
                        src={img.preview}
                        style={{ height: "150px", objectFit: "cover" }}
                      />
                      <Card.Body>
                        <Form.Control
                          placeholder="Enter image description"
                          value={img.description}
                          onChange={(e) =>
                            handleNewImageChange(index, "description", e.target.value)
                          }
                          className="mb-2"
                        />
                        {img.progress > 0 && (
                          <ProgressBar
                            now={img.progress}
                            label={`${img.progress}%`}
                            className="mb-2"
                          />
                        )}
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => removeNewImage(index)}
                        >
                          Remove
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => setShowModal(false)}
            >
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm">
              {editingService ? "Update" : "Save"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* ✅ Slideshow Modal */}
      <Modal
        show={showSlideshow}
        onHide={() => setShowSlideshow(false)}
        fullscreen
        className="slideshow-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Service Images Slideshow</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
          <Carousel
            ref={carouselRef}
            indicators={true}
            controls={true}
            prevIcon={<ChevronLeft size={30} style={{ color: "limegreen" }} />}
            nextIcon={<ChevronRight size={30} style={{ color: "limegreen" }} />}
            style={{ width: "100%", maxWidth: "100vw", maxHeight: "100vh" }}
          >
            {currentImages.map((img) => (
              <Carousel.Item key={img.id}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "calc(100vh - 60px)", // Adjust for header/footer
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={`${API_BASE_URL}/uploads/${editingService?.id}/${img.image_path}`}
                    alt={img.description}
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </div>
                <Carousel.Caption>
                  <h3>{img.description || "No description"}</h3>
                </Carousel.Caption>
              </Carousel.Item>
            ))}
          </Carousel>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ManageServices;